// główny layout

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, Outlet } from 'react-router-dom';
import { ChevronUp } from 'react-bootstrap-icons';
import classnames from 'classnames';

import { useRWD } from 'hooks';
import { useSelector } from 'store';
import {
  Footer,
  HeaderMain,
  HeaderBottomBar,
  HeaderTopBar,
  BottomMenu,
  MenuPopup
} from 'components/layouts/MainLayout/components';
import DockedChatContainer from 'components/containers/DockedChatContainer';
import HorizontalChatContainer from 'components/containers/HorizontalChatContainer';

import styles from 'theme/components/layouts/MainLayout/Main.module.scss';

const Main = () => {
  const location = useLocation();
  const { profile } = useSelector((state) => state.auth);
  const { isMobileMenu } = useSelector((state) => state.ui);
  const { isChatDocked, isChatHorizontal, isChatOpen } = useSelector((state) => state.chat);

  const withDockedChat = isChatDocked && isChatOpen;
  const withHorizontalChat = isChatHorizontal && isChatOpen;

  const { isMobile } = useRWD();

  // ref głównego kontentu aplikacji
  const scrollableContentRef = useRef<HTMLDivElement>(null);

  // czy jest widoczny przycisk powrotu na górę strony
  const [isBackToTopButton, setIsBackToTopButton] = useState(false);

  // funkcja skrolująca stronę do góry
  const scrollToTop = useCallback(
    () => scrollableContentRef.current?.scrollTo({ top: 0, left: 0, behavior: 'smooth' }),
    [scrollableContentRef.current]
  );

  // przeskrolowanie strony do góry przy przejściu między stronami
  useEffect(() => {
    scrollToTop();
  }, [scrollableContentRef.current, location.key]);

  const renderHeader = () => {
    const pathSegments = location.pathname.split('/').filter((item) => item);
    // Sprawdzenie czy ścieżka zawiera 'knowledge-base' (uwzględniając prefiks języka)
    const isKnowledgeBasePage = pathSegments.includes('knowledge-base');
    
    // Mobile: Header widoczny dla strony głównej (0-1 segmenty) LUB dla knowledge-base
    if (!isMobile || pathSegments.length < 2 || isKnowledgeBasePage) {
      return (
        <header>
          <HeaderTopBar />
          <HeaderMain />
          <HeaderBottomBar />
        </header>
      );
    }

    return null;
  };

  const renderContent = () => (
    <div
      ref={scrollableContentRef}
      onScroll={() =>
        scrollableContentRef.current?.scrollTop && scrollableContentRef.current?.scrollTop > 500
          ? setIsBackToTopButton(true)
          : setIsBackToTopButton(false)
      }
      // Ta klasa jest używana w innych komponentach - nie zmieniać!
      className={classnames('scrollableContent', styles.content)}>
      <main>
        {renderHorizontalChat()}
        <Outlet />
      </main>
      <footer>
        <Footer />
      </footer>
      <BottomMenu />
    </div>
  );

  const renderMenuPopup = () => {
    if (isMobile && isMobileMenu && profile?.role !== 'ROLE_OPEN_PROFILE') {
      return <MenuPopup />;
    }

    return null;
  };

  const renderDockedChat = () => {
    if (withDockedChat && profile?.role !== 'ROLE_OPEN_PROFILE') {
      return <DockedChatContainer />;
    }

    return null;
  };

  const renderHorizontalChat = () => {
    if (withHorizontalChat && profile?.role !== 'ROLE_OPEN_PROFILE') {
      return <HorizontalChatContainer />;
    }

    return null;
  };

  const renderBackToTopButton = () => {
    if (isBackToTopButton) {
      return (
        <button onClick={scrollToTop} className={styles.backToTop}>
          <ChevronUp />
        </button>
      );
    }

    return null;
  };

  return (
    <div
      className={classnames(
        styles.componentWrapper,
        'StylePath-Components-Layouts-MainLayout-Main',
        {
          [styles.withDockedChat]: withDockedChat,
          [styles.withHorizontalChat]: withHorizontalChat
        }
      )}>
      <div className={styles.mainWrapper}>
        <div className={styles.headerAndContent}>
          {renderHeader()}
          {renderContent()}
        </div>
        {renderDockedChat()}
      </div>
      {renderMenuPopup()}
      {renderBackToTopButton()}
    </div>
  );
};

export default Main;
